import { FilterSearchInput } from '@/components/filters/filter-search-input';
import { mediaQuery } from '@/theme/setupTheme';
import { FlexContainer } from '@/ui/flexbox/flexbox';
import { SpotlightButton } from '@/ui/spotlight';
import { styled } from 'goober';

export const ControlsSection = styled('div')`
  display: grid;
  gap: 32px;
`;

export const ControlsRow = styled(FlexContainer)`
  width: 100%;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 980px) {
    gap: 10px;
  }
`;

export const SearchControl = styled('div')`
  flex: 1;
  min-width: 320px;

  ${mediaQuery('xl', 'min')} {
    max-width: 720px;
  }

  @media (max-width: 980px) {
    min-width: 100%;
  }
`;

export const SearchInput = styled(FilterSearchInput)`
  width: 100%;

  & > div {
    width: 100%;
  }

  & label {
    border: 1px solid ${({ theme }) => theme.colors.spotlight.border.default};
    border-radius: 8px;
    gap: 8px;
    padding: 10px 12px;
    ${({ theme }) =>
      theme.mixins.transition(
        ['border-color'],
        theme.duration.normal,
        theme.easing.out,
      )}
  }

  & input {
    ${({ theme }) => theme.typography.spotlight.controlButton}
    color: ${({ theme }) => theme.colors.spotlight.text.primary};
  }

  &:focus-within label {
    border-color: ${({ theme }) => theme.colors.spotlight.primary};
  }
`;

export const SortingOptionLabel = styled('span')`
  align-items: center;
  display: inline-flex;
  gap: 8px;
`;

export const SortingOptionEmoji = styled('span')`
  display: inline-flex;
  font-size: 16px;
  line-height: 1;
`;

export const ControlButton = styled(SpotlightButton)`
  && {
    ${({ theme }) => theme.typography.spotlight.controlButton}
    background-color: ${({ theme }) => theme.colors.basics.white};
    border: 1px solid ${({ theme }) => theme.colors.spotlight.border.default};
    border-radius: 8px;
    color: ${({ theme }) => theme.colors.spotlight.text.primary};
    gap: 8px;
    justify-content: center;
    ${({ theme }) =>
      theme.mixins.transition(
        ['border-color', 'box-shadow'],
        theme.duration.normal,
        theme.easing.out,
      )}
  }

  &[data-is-active='true'] {
    background: ${({ theme }) => theme.colors.spotlight.primary};
    border-color: ${({ theme }) => theme.colors.spotlight.primary};
    color: ${({ theme }) => theme.colors.basics.white};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.spotlight.border.focus};
    outline-offset: 1px;
  }

  &:hover:not(:disabled):not([data-is-active='true']) {
    background: ${({ theme }) => theme.colors.spotlight.surface.subtle};
    border-color: ${({ theme }) => theme.colors.spotlight.primary};
    color: ${({ theme }) => theme.colors.spotlight.text.primary};
  }

  &[data-is-active='true']:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.spotlight.primary};
    border-color: ${({ theme }) => theme.colors.spotlight.primary};
    color: ${({ theme }) => theme.colors.basics.white};
  }

  @media (max-width: 980px) {
    width: 100%;
  }
`;

export const FilterButtonContainer = styled('div')``;

export const StageFiltersContainer = styled('div')`
  ${({ theme }) =>
    theme.animations.enterFadeSlideDown('-8px', theme.duration.normal)}
  background: ${({ theme }) => theme.colors.basics.white};
  border: 1px solid ${({ theme }) => theme.colors.spotlight.border.default};
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.shadow.spotlight.filters};
  display: grid;
  gap: 32px;
  padding: 24px;
  width: 100%;
`;

export const StageFiltersTitle = styled('p')`
  ${({ theme }) => theme.typography.spotlight.filterTitle}
  color: ${({ theme }) => theme.colors.spotlight.text.primary};
  margin: 0;
`;

export const StageFiltersRow = styled('div')`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

export const StageFilterTagButton = styled('button')<{ $isSelected: boolean }>`
  ${({ theme }) => theme.typography.spotlight.filterTag}
  background: ${({ theme, $isSelected }) =>
    $isSelected ? theme.colors.spotlight.primary : theme.colors.basics.white};
  border: 1px solid
    ${({ theme, $isSelected }) =>
      $isSelected
        ? theme.colors.spotlight.primary
        : theme.colors.spotlight.border.default};
  border-radius: 999px;
  color: ${({ theme, $isSelected }) =>
    $isSelected
      ? theme.colors.basics.white
      : theme.colors.spotlight.text.primary};
  cursor: pointer;
  min-height: 36px;
  padding: 8px 16px;
  ${({ theme }) =>
    theme.mixins.transition(
      ['background-color', 'border-color', 'color'],
      theme.duration.normal,
      theme.easing.out,
    )}

  &:hover {
    border-color: ${({ theme }) => theme.colors.spotlight.primary};
    color: ${({ theme, $isSelected }) =>
      $isSelected ? theme.colors.basics.white : theme.colors.spotlight.primary};
  }
`;

export const StageFiltersFooter = styled('div')`
  display: grid;
  gap: 14px;
`;

export const StageFiltersDivider = styled('div')`
  border-top: 1px solid ${({ theme }) => theme.colors.spotlight.border.default};
  margin: 0 -24px;
`;

export const ClearFiltersButton = styled('button')`
  ${({ theme }) => theme.typography.spotlight.clearFilters}
  background: transparent;
  border: 0;
  color: ${({ theme }) => theme.colors.spotlight.primary};
  cursor: pointer;
  justify-self: start;
  margin: 0;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

export const ShowingCompanies = styled('p')`
  ${({ theme }) => theme.typography.spotlight.resultCount}
  color: ${({ theme }) => theme.colors.spotlight.text.secondary};
  margin: 0;
`;
